import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import styles from './bar-chart.scss';

interface BarChangeProps {
  activeBar: HTMLButtonElement | null;
  activeIdx: number;
}

interface BarChartProps {
  ariaLabels?: string[];
  initialValueIdx?: number;
  labels?: string[];
  maxValue: number;
  onBarChange?: ({ activeBar, activeIdx }: BarChangeProps) => void;
  onBarClick?: (value: number, index: number) => void;
  selectedBarMessage?: ReactNode;
  values: number[];
}

interface MessagePosition {
  marginLeft?: string | number;
}

function BarChart(props: BarChartProps) {
  const {
    ariaLabels,
    initialValueIdx = 0,
    labels,
    maxValue,
    onBarChange,
    onBarClick,
    selectedBarMessage,
    values
  } = props;

  const selectedBarRef = useRef<HTMLButtonElement>(null);
  const selectedBarMsgRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(initialValueIdx);
  const [messagePosition, setMessagePosition] = useState<MessagePosition>();

  const calcMessagePosition = useCallback(
    (activeBar = selectedBarRef.current) => {
      if (selectedBarMessage && activeBar) {
        const newMsgPos: MessagePosition = {};
        const currentBarMsgRef = selectedBarMsgRef.current;
        const barChartWidth =
          currentBarMsgRef?.parentElement?.getBoundingClientRect().width;
        let messageWidth;

        if (typeof selectedBarMessage === 'string') {
          messageWidth = currentBarMsgRef?.getBoundingClientRect().width;
        } else {
          messageWidth =
            currentBarMsgRef?.children[0].getBoundingClientRect().width;
        }

        if (
          barChartWidth !== undefined &&
          messageWidth + activeBar.offsetLeft <= barChartWidth
        ) {
          newMsgPos.marginLeft = activeBar.offsetLeft;
        } else if (barChartWidth !== undefined) {
          newMsgPos.marginLeft = barChartWidth - messageWidth;
        }

        setMessagePosition(newMsgPos);
        if (onBarChange) onBarChange({ activeBar, activeIdx });
      }
    },
    [activeIdx, onBarChange, selectedBarMessage]
  );

  function handleClick(data, e) {
    setActiveIdx(data.idx);
    calcMessagePosition(e.target);
    if (onBarClick) onBarClick(data, e);
  }

  useEffect(() => {
    window.addEventListener('resize', calcMessagePosition);
    return () => window.removeEventListener('resize', calcMessagePosition);
  }, [calcMessagePosition]);

  useEffect(() => {
    calcMessagePosition();
  }, [calcMessagePosition, selectedBarMessage]);

  const barsTotalWidthPercentage = 100 - (values.length - 1);
  const barWidth = barsTotalWidthPercentage / values.length;

  return (
    <div className={styles['bar-chart']}>
      <div ref={selectedBarMsgRef} style={messagePosition}>
        {selectedBarMessage}
      </div>
      <div className={styles['values']}>
        {values.map((value, idx) => {
          const height = `${(value / maxValue) * 100}%`;
          const isActive = idx === activeIdx;

          return (
            <button
              aria-label={ariaLabels && ariaLabels[idx]}
              className={isActive ? styles['bar-active'] : styles['bar']}
              data-testid={`bar-chart-bar-${idx}`}
              key={`bar-chart-bar-${idx}`}
              onClick={e => handleClick({ idx, value }, e)}
              ref={isActive ? selectedBarRef : null}
              style={{ height, width: `${barWidth}%` }}
            >
              &nbsp;
            </button>
          );
        })}
      </div>
      <div className={styles['labels']}>
        {labels?.map((label, idx) => (
          <div className={styles['label']} key={`bar-chart-label-${idx}`}>
            {label}
          </div>
        ))}
      </div>
    </div>
  );
}

export default BarChart;
